import Axios, { AxiosError } from 'axios';
import { ApiCall } from '../http-service/types/ApiCall';

type DynamicApiCall<T> = (
  ...args: [...props: any[], abortSignal: AbortSignal]
) => Promise<T>;
/**
 * @description
 * Class used for request cancelation.
 *
 * Instance of this class used for requests made outside of components (f.i. redux actions)
 *
 * For using in components/custom hooks use useCancellableRequest {@see ./hook.js}
 *
 *
 * @example
 * Usage in redux actions
 * const request = new CancellableRequest();
 * //Note: instance of req should be created outside of action
 * const fetchCarriersActions = (portalId) => dispatch => {
 *  request.call(getCarriers, { portal_id: value }).
 *    then(res => dispatch({ type: 'SET_CARRIERS', payload: res }))
 *  }
 *
 * @example
 * Checking if request was canceled
 * const request = new CancellableRequest();
 * request.call(getCarriers, { portal: 1 }).catch(err => {
 *  if(!CancellableRequest.isCancelled(err)) {
 *    setError(err)
 *  }
 * })
 */

export default class CancellableRequest {
  abortController?: AbortController;
  /**
   * Generates new AbortController which can be used for any request
   */
  static generateAbortController() {
    return new AbortController();
  }

  /**
   * Determines if error caused by request cancellation
   */
  static isCancelled(err: AxiosError) {
    return Axios.isCancel(err);
  }

  /**
   * Cancels request(s) where abort signal was passed.
   * Generates new abort controller after cancellation
   */
  cancel(): AbortController {
    if (this.abortController) {
      this.abortController.abort();
    }
    const abortController = CancellableRequest.generateAbortController();
    this.abortController = abortController;
    return abortController;
  }

  /**
   * @deprecated - use **send** method instead
   * Make request call, api call params are not controlled by typescript so be aware of passing them
   */
  call<T>(apiCall: DynamicApiCall<T>, ...params: any[]): Promise<T> {
    const abortController = this.cancel();
    return apiCall(...params, abortController.signal);
  }

  /**
   * Make request call. 
   */
  send<CallParams, CallResponse>(
    apiCall: ApiCall<CallParams, CallResponse>,
    params: CallParams
  ): Promise<CallResponse> {
    const abortController = this.cancel();
    return apiCall({ params, abortSignal: abortController.signal });
  }
}
